import React from 'react';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';

import { ALLOWED_FLAG_MESSAGES } from './allowedFlagConstants';
import { getPageHistoryLink } from './getPageHistoryLink';

const i18n = {
	publishTitle: {
		id: 'editor-flags.publish-page.title',
		defaultMessage: 'New version published',
		description: 'Title for flag that appears when a new version of the page is published',
	},
	publishMessage: {
		id: 'editor-flags.publish-page.known.user.message',
		defaultMessage:
			'{user} just published a new version of this page, which includes the changes you’ve made so far',
		description: 'Message for flag that appears when a new version of the page is published',
	},
	restoreTitle: {
		id: 'editor-flags.restore-page.title',
		defaultMessage: 'Previous version restored',
		description:
			'Title for flag that appears when another user restores a previous version of the page',
	},
	restoreOtherMessage: {
		id: 'editor-flags.restore-page.known.user.message',
		defaultMessage:
			'{user} just restored a prior version of this {contentType}. Recent changes may have been lost. Check the <alink>page history</alink> for details.',
		description:
			'Message for flag that apprears when another user restores a previous version of the page',
	},
	unknownUser: {
		id: 'editor-flags.publish-page.known.user.name',
		defaultMessage: 'Another user',
		description: 'Alternate name when the user responsible for publish is not known',
	},
	anonymousUser: {
		id: 'editor-flags.publish-page.anonymous.user.name',
		defaultMessage: 'An anonymous user',
		description: 'Alternate name when anonymous user is responsible for publish',
	},
	reloadButton: {
		id: 'editor-flags.publish-page.actions.reload',
		description: 'Button label for button that causes a page reload',
		defaultMessage: 'Reload',
	},
};

const reloadPage = () => window.location.reload();

const displayLivePagePublishedFlag = (
	flags: FlagsStateContainer,
	formatMessage: (...args: any[]) => any,
	user: string,
): void => {
	void flags.showCustomFlag({
		title: formatMessage(i18n.publishTitle),
		description: formatMessage(i18n.publishMessage, { user }),
		actions: [
			{
				content: (
					<div data-testid="quick-reload-reload-button">{formatMessage(i18n.reloadButton)}</div>
				),
				onClick: reloadPage,
			},
		],
	});
};

const getUserMessage = (formatMessage: (...args: any[]) => any, queryResult): string => {
	let user: string;
	const displayName = queryResult?.data?.user?.displayName;
	/* There are 3 cases:
      1. error when making query : the other publisher is unknown and therefore default to "Another user"
      2. queryResult?.data?.user?.displayName is falsy but there is no error, the other publisher is anonymous user
      3. The other publisher is authenticated and there's no error in making query
     */
	if (queryResult.error) {
		// error when making query, publisher is unknown and therefore
		user = formatMessage(i18n.unknownUser);
	} else if (!displayName) {
		// queryResult data is null, publisher is anonymous user
		user = formatMessage(i18n.anonymousUser);
	} else {
		user = displayName;
	}
	return user;
};

const handlePublishFlag = (
	flags: FlagsStateContainer,
	formatMessage: (...args: any[]) => any,
	isLivePage: boolean,
	user: string,
) => {
	if (isLivePage) {
		displayLivePagePublishedFlag(flags, formatMessage, user);
	} else {
		void flags.showInfoFlag({
			title: formatMessage(i18n.publishTitle),
			description: formatMessage(i18n.publishMessage, { user }),
		});
	}
};

export const initializeInfoFlag =
	(
		flags: FlagsStateContainer,
		formatMessage: (...args: any[]) => any,
		attributes: any,
		isLivePage: boolean = false,
	) =>
	(queryResult, flagData) => {
		if (!queryResult.loading) {
			const user = getUserMessage(formatMessage, queryResult);

			if (flagData[ALLOWED_FLAG_MESSAGES.OTHER_PUBLISH]) {
				handlePublishFlag(flags, formatMessage, isLivePage, user);
				return;
			} else if (flagData[ALLOWED_FLAG_MESSAGES.OTHER_RESTORE]) {
				void flags.showInfoFlag({
					title: formatMessage(i18n.restoreTitle),
					description: formatMessage(i18n.restoreOtherMessage, {
						user,
						alink: getPageHistoryLink(attributes),
						contentType: attributes.contentType ?? 'page',
					}),
				});
			}
		}
		return null;
	};
